import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { DepositDisclaimer, FormNotice } from "@/components/site/Notices";
import ConsentCheckbox from "@/components/site/ConsentCheckbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, X, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { PACKAGE_GROUPS } from "@/lib/packages";

const FEATURE_OPTIONS = [
  "Contact Form",
  "Blog",
  "Online Store / Ecommerce",
  "Appointment Booking",
  "Customer Portal",
  "Membership Area",
  "CRM Integration",
  "CMS Access",
  "AI Features",
  "API Integrations",
  "Payment Processing",
  "Mobile App",
  "User Accounts/Login",
  "Custom Dashboard",
  "File Uploads",
  "Email Marketing Integration",
  "Analytics & Tracking",
  "Other",
];

const BUDGET_RANGES = [
  "Under $1,000",
  "$1,000 - $2,500",
  "$2,500 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
];

const YES_NO = ["Yes", "No"];

const DISCOUNT_OPTIONS = [
  "Military or Veteran",
  "First Responder",
  "Teacher",
  "Healthcare Worker",
  "Not applicable",
];

function findPkg(id) {
  for (const g of PACKAGE_GROUPS) {
    const found = g.packages.find((p) => p.id === id);
    if (found) return { group: g, pkg: found };
  }
  return null;
}

const EMPTY_FORM = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  industry: "",
  company_description: "",
  existing_website: "",
  social_links: "",
  desired_launch_date: "",
  budget_range: "",
  primary_goal: "",
  features: [],
  has_branding: "",
  has_logo: "",
  preferred_colors: "",
  websites_liked: "",
  design_notes: "",
  needs_hosting: "",
  needs_maintenance: "",
  needs_support: "",
  discount_eligibility: "",
  referral_name: "",
  referral_code: "",
  project_description: "",
  special_requirements: "",
};

const inputCls = "bg-white/5 border-white/10 text-white";

export default function Intake() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const meta = useMemo(() => findPkg(packageId), [packageId]);
  const storageKey = `eq_intake_draft_${packageId}`;
  const [form, setForm] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`eq_intake_draft_${packageId}`);
      if (saved) return { ...EMPTY_FORM, ...(JSON.parse(saved).form || {}) };
    } catch (_) { /* ignore */ }
    return EMPTY_FORM;
  });
  const [files, setFiles] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`eq_intake_draft_${packageId}`);
      if (saved) return JSON.parse(saved).files || [];
    } catch (_) { /* ignore */ }
    return [];
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (!meta) {
      toast.error("Unknown package");
      navigate("/packages", { replace: true });
    }
  }, [meta, navigate]);

  // Persist the draft so returning from the next step keeps the inputs
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ form, files }));
    } catch (_) { /* ignore */ }
  }, [form, files, storageKey]);

  if (!meta) return null;
  const { pkg, group } = meta;

  const backToPackages = () => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch (_) { /* ignore */ }
    navigate("/packages");
  };

  const clearError = (k) => setErrors((e) => (e[k] ? { ...e, [k]: false } : e));

  const setField = (k) => (eOrVal) => {
    const value = eOrVal?.target ? eOrVal.target.value : eOrVal;
    setForm((f) => ({ ...f, [k]: value }));
    clearError(k);
  };

  const toggleFeature = (label) => {
    clearError("features");
    setForm((f) => {
      const has = f.features.includes(label);
      return { ...f, features: has ? f.features.filter((x) => x !== label) : [...f.features, label] };
    });
  };

  // Returns input classes + red highlight when the field is flagged as missing
  const fieldCls = (key, extra = "") =>
    `${inputCls}${extra ? " " + extra : ""}${
      errors[key] ? " eq-field-error !border-red-500/80 ring-1 ring-red-500/50" : ""
    }`;

  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    try {
      setUploading(true);
      const uploaded = [];
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      }
      setFiles((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try a smaller file.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (path) => setFiles((prev) => prev.filter((f) => f.path !== path));

  const submit = async (e) => {
    e.preventDefault();

    // Required-field validation -> collect missing fields and highlight them
    const required = Object.keys(EMPTY_FORM).filter((k) => k !== "features");
    const nextErrors = {};
    for (const k of required) {
      if (!String(form[k] || "").trim()) nextErrors[k] = true;
    }
    if (form.features.length === 0) nextErrors.features = true;

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      toast.error("Please complete the highlighted fields. Use N/A or Unknown where it does not apply.");
      setTimeout(() => {
        const el = formRef.current?.querySelector(".eq-field-error");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
      return;
    }

    if (!consent) {
      toast.error("Please review and agree to the Terms & Conditions to continue.");
      return;
    }

    try {
      setSubmitting(true);
      const { data: created } = await api.post("/intakes", {
        package_id: pkg.id,
        ...form,
        file_paths: files.map((f) => f.path),
        consent_agreement: true,
      });
      navigate(`/intake/${pkg.id}/next`, { state: { intakeId: created.id } });
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`page-intake-${pkg.id}`}>
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <button type="button" onClick={backToPackages} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6 transition-colors" data-testid="intake-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to packages
          </button>

          <SectionHeader
            eyebrow={group.title}
            title={`${pkg.name} — Project Intake`}
            description="Tell us about your project so we can begin work. The more detail you provide, the smoother the build."
          />

          <div className="space-y-4 mb-8">
            <DepositDisclaimer />
            <FormNotice />
          </div>

          <form ref={formRef} onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 space-y-10" data-testid="intake-form">
            {/* Contact */}
            <Group title="Contact Information">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *"><Input data-testid="intake-full-name" value={form.full_name} onChange={setField("full_name")} className={fieldCls("full_name")} placeholder="Your full name" /></Field>
                <Field label="Business Name *"><Input data-testid="intake-business-name" value={form.business_name} onChange={setField("business_name")} className={fieldCls("business_name")} placeholder="Company name or N/A" /></Field>
                <Field label="Email Address *"><Input data-testid="intake-email" type="email" value={form.email} onChange={setField("email")} className={fieldCls("email")} placeholder="you@business.com" /></Field>
                <Field label="Phone Number *"><Input data-testid="intake-phone" value={form.phone} onChange={setField("phone")} className={fieldCls("phone")} placeholder="(555) 000-1234" /></Field>
              </div>
            </Group>

            {/* Business */}
            <Group title="Business Information">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Industry *"><Input data-testid="intake-industry" value={form.industry} onChange={setField("industry")} className={fieldCls("industry")} placeholder="e.g. Real estate, fitness" /></Field>
                <Field label="Existing Website URL *"><Input data-testid="intake-existing-website" value={form.existing_website} onChange={setField("existing_website")} className={fieldCls("existing_website")} placeholder="https://... or N/A" /></Field>
                <div className="sm:col-span-2">
                  <Field label="Company Description *"><Textarea rows={3} data-testid="intake-company-description" value={form.company_description} onChange={setField("company_description")} className={fieldCls("company_description")} placeholder="What does your company do?" /></Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Social Media Links *"><Textarea rows={2} data-testid="intake-social-links" value={form.social_links} onChange={setField("social_links")} className={fieldCls("social_links")} placeholder="Instagram, LinkedIn, Facebook URLs — or N/A" /></Field>
                </div>
              </div>
            </Group>

            {/* Project */}
            <Group title="Project Details">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Package Selected">
                  <Input value={pkg.name} disabled data-testid="intake-package-selected" className={`${inputCls} opacity-80 cursor-not-allowed`} />
                </Field>
                <Field label="Desired Launch Date *">
                  <Input data-testid="intake-launch-date" type="date" value={form.desired_launch_date} onChange={setField("desired_launch_date")} className={fieldCls("desired_launch_date", "[color-scheme:dark]")} />
                </Field>
                <Field label="Budget Range *">
                  <Select value={form.budget_range} onValueChange={setField("budget_range")}>
                    <SelectTrigger data-testid="intake-budget" className={fieldCls("budget_range")}><SelectValue placeholder="Select budget" /></SelectTrigger>
                    <SelectContent>
                      {BUDGET_RANGES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Primary Goal of the Project *">
                  <Input data-testid="intake-primary-goal" value={form.primary_goal} onChange={setField("primary_goal")} className={fieldCls("primary_goal")} placeholder="e.g. generate leads, reduce manual work" />
                </Field>
              </div>
            </Group>

            {/* Features */}
            <Group title="Features Needed" subtitle="Select all that apply (at least one).">
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 ${errors.features ? "eq-field-error rounded-2xl ring-1 ring-red-500/50 p-3" : ""}`}>
                {FEATURE_OPTIONS.map((opt) => {
                  const checked = form.features.includes(opt);
                  return (
                    <label
                      key={opt}
                      data-testid={`intake-feature-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 border cursor-pointer transition-all text-sm ${
                        checked ? "border-blue-500/60 bg-blue-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/20"
                      }`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleFeature(opt)} />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            </Group>

            {/* Design */}
            <Group title="Design Preferences">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Do you have branding already? *">
                  <Select value={form.has_branding} onValueChange={setField("has_branding")}>
                    <SelectTrigger data-testid="intake-has-branding" className={fieldCls("has_branding")}><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>
                      {YES_NO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Do you have a logo? *">
                  <Select value={form.has_logo} onValueChange={setField("has_logo")}>
                    <SelectTrigger data-testid="intake-has-logo" className={fieldCls("has_logo")}><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>
                      {YES_NO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Preferred Colors *">
                  <Input data-testid="intake-preferred-colors" value={form.preferred_colors} onChange={setField("preferred_colors")} className={fieldCls("preferred_colors")} placeholder="e.g. navy + electric blue, or N/A" />
                </Field>
                <Field label="Websites You Like *">
                  <Input data-testid="intake-websites-liked" value={form.websites_liked} onChange={setField("websites_liked")} className={fieldCls("websites_liked")} placeholder="URLs of sites you admire, or N/A" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Additional Design Notes *">
                    <Textarea rows={3} data-testid="intake-design-notes" value={form.design_notes} onChange={setField("design_notes")} className={fieldCls("design_notes")} placeholder="Style direction, must-avoid, etc." />
                  </Field>
                </div>
              </div>
            </Group>

            {/* Hosting & Maintenance */}
            <Group title="Hosting & Maintenance">
              <div className="grid sm:grid-cols-3 gap-5">
                <Field label="Need hosting? *">
                  <Select value={form.needs_hosting} onValueChange={setField("needs_hosting")}>
                    <SelectTrigger data-testid="intake-needs-hosting" className={fieldCls("needs_hosting")}><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>
                      {YES_NO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Interested in maintenance? *">
                  <Select value={form.needs_maintenance} onValueChange={setField("needs_maintenance")}>
                    <SelectTrigger data-testid="intake-needs-maintenance" className={fieldCls("needs_maintenance")}><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>
                      {YES_NO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Interested in ongoing support? *">
                  <Select value={form.needs_support} onValueChange={setField("needs_support")}>
                    <SelectTrigger data-testid="intake-needs-support" className={fieldCls("needs_support")}><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>
                      {YES_NO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Group>

            {/* Discounts + Referral */}
            <Group title="Discounts & Referral Program">
              <Field label="Discount Eligibility *">
                <div className={`grid grid-cols-2 md:grid-cols-3 gap-3 ${errors.discount_eligibility ? "eq-field-error rounded-2xl ring-1 ring-red-500/50 p-3" : ""}`}>
                  {DISCOUNT_OPTIONS.map((opt) => {
                    const checked = form.discount_eligibility === opt;
                    return (
                      <label
                        key={opt}
                        className={`flex items-center gap-3 rounded-xl px-4 py-3 border cursor-pointer transition-all text-sm ${
                          checked ? "border-blue-500/60 bg-blue-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/20"
                        }`}
                        data-testid={`intake-discount-${opt.replace(/[/\s]+/g, "-").toLowerCase()}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => setField("discount_eligibility")(opt)} />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="Referral Name *">
                  <Input data-testid="intake-referral-name" value={form.referral_name} onChange={setField("referral_name")} className={fieldCls("referral_name")} placeholder="Who referred you? or N/A" />
                </Field>
                <Field label="Referral Code *">
                  <Input data-testid="intake-referral-code" value={form.referral_code} onChange={setField("referral_code")} className={fieldCls("referral_code")} placeholder="Code or N/A" />
                </Field>
              </div>
            </Group>

            {/* Notes */}
            <Group title="Additional Notes">
              <Field label="Project Description *">
                <Textarea rows={4} data-testid="intake-project-description" value={form.project_description} onChange={setField("project_description")} className={fieldCls("project_description")} placeholder="Goals, audience, success criteria..." />
              </Field>
              <div className="mt-5">
                <Field label="Special Requirements *">
                  <Textarea rows={3} data-testid="intake-special-requirements" value={form.special_requirements} onChange={setField("special_requirements")} className={fieldCls("special_requirements")} placeholder="Compliance, accessibility, integrations, deadlines... or N/A" />
                </Field>
              </div>

              <div className="mt-5">
                <Label className="text-gray-300 text-sm">Attach Files (optional)</Label>
                <label htmlFor="intake-file-input" className="mt-3 flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-8 text-center cursor-pointer hover:border-blue-500/40 transition-colors" data-testid="intake-file-dropzone">
                  <Upload className="w-6 h-6 text-blue-400" />
                  <div className="text-sm text-gray-300">{uploading ? "Uploading..." : "Click to upload files"}</div>
                  <div className="text-xs text-gray-500">PDF, images, briefs · Max 15MB each · Optional</div>
                  <input id="intake-file-input" type="file" multiple onChange={handleUpload} className="hidden" data-testid="intake-file-input" />
                </label>
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {files.map((f) => (
                      <li key={f.path} className="flex items-center justify-between bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm">
                        <span className="truncate">{f.filename}</span>
                        <button type="button" onClick={() => removeFile(f.path)} className="text-gray-400 hover:text-red-400">
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Group>

            {Object.values(errors).some(Boolean) && (
              <p className="text-sm text-red-400" data-testid="intake-error-summary">
                Some required fields are missing. The highlighted fields above need to be completed (use N/A where it doesn't apply).
              </p>
            )}

            <ConsentCheckbox checked={consent} onChange={setConsent} testid="intake-consent" />

            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="intake-submit-btn">
              {submitting ? "Submitting..." : "Continue"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Group({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-5">
        <h3 className="font-display text-xl font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        <div className="mt-3 h-px bg-gradient-to-r from-white/20 to-transparent" />
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-gray-300 text-sm">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
